import { cn } from '@/lib/utils.js';
import type { ColumnType } from '@quartup/reports-shared';

interface CellProps {
  value: unknown;
  type: ColumnType;
  currency?: string | undefined;
  decimals?: number | undefined;
  align?: 'left' | 'center' | 'right' | undefined;
}

const DATE_FMT = new Intl.DateTimeFormat('es-ES', { dateStyle: 'short' });
const MONTH_FMT = new Intl.DateTimeFormat('es-ES', { year: 'numeric', month: 'short' });
const NUMBER_FMT = new Intl.NumberFormat('es-ES');
const PERCENT_FMT = new Intl.NumberFormat('es-ES', { style: 'percent', maximumFractionDigits: 2 });

function moneyFormatter(currency: string, decimals?: number) {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency,
    minimumFractionDigits: decimals ?? 2,
    maximumFractionDigits: decimals ?? 2,
  });
}

function numberFormatter(decimals?: number) {
  if (decimals === undefined) return NUMBER_FMT;
  return new Intl.NumberFormat('es-ES', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function formatDate(raw: unknown): string {
  if (raw === null || raw === undefined || raw === '') return '—';
  try {
    // Accept ISO (YYYY-MM-DD) and DD-MM-YYYY
    const s = String(raw);
    let d: Date;
    if (/^\d{2}-\d{2}-\d{4}$/.test(s)) {
      const [dd, mm, yyyy] = s.split('-');
      d = new Date(`${yyyy}-${mm}-${dd}`);
    } else {
      d = new Date(s);
    }
    if (Number.isNaN(d.getTime())) return s;
    return DATE_FMT.format(d);
  } catch {
    return String(raw);
  }
}

function formatMonth(raw: unknown): string {
  if (raw === null || raw === undefined || raw === '') return '—';
  try {
    const s = String(raw);
    const d = new Date(s.length === 7 ? `${s}-01` : s);
    if (Number.isNaN(d.getTime())) return s;
    return MONTH_FMT.format(d);
  } catch {
    return String(raw);
  }
}

export function ResultsTableCell({ value, type, currency, decimals, align }: CellProps) {
  const alignClass =
    align === 'right' ||
    type === 'money' ||
    type === 'number' ||
    type === 'integer' ||
    type === 'percent'
      ? 'text-right'
      : align === 'center'
        ? 'text-center'
        : 'text-left';

  if (value === null || value === undefined) {
    return (
      <span className={cn('text-[var(--color-qup-text-muted-dark)] tabular-nums', alignClass)}>
        —
      </span>
    );
  }

  switch (type) {
    case 'date':
      return <span className={cn('tabular-nums', alignClass)}>{formatDate(value)}</span>;

    case 'datetime':
      try {
        const d = new Date(String(value));
        return (
          <span className={cn('tabular-nums', alignClass)}>
            {Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString('es-ES')}
          </span>
        );
      } catch {
        return <span className={alignClass}>{String(value)}</span>;
      }

    case 'month':
      return <span className={cn('tabular-nums', alignClass)}>{formatMonth(value)}</span>;

    case 'money':
      try {
        const n = typeof value === 'number' ? value : Number(value);
        const fmt = moneyFormatter(currency ?? 'EUR', decimals);
        return (
          <span className={cn('tabular-nums font-medium', alignClass)}>
            {Number.isNaN(n) ? String(value) : fmt.format(n)}
          </span>
        );
      } catch {
        return <span className={alignClass}>{String(value)}</span>;
      }

    case 'number':
    case 'integer': {
      const n = typeof value === 'number' ? value : Number(value);
      const fmt = numberFormatter(type === 'integer' ? 0 : decimals);
      return (
        <span className={cn('tabular-nums', alignClass)}>
          {Number.isNaN(n) ? String(value) : fmt.format(n)}
        </span>
      );
    }

    case 'percent': {
      const n = typeof value === 'number' ? value : Number(value);
      return (
        <span className={cn('tabular-nums', alignClass)}>
          {Number.isNaN(n) ? String(value) : PERCENT_FMT.format(n / 100)}
        </span>
      );
    }

    case 'boolean':
      return <span className={alignClass}>{value === true || value === 'true' ? 'Sí' : 'No'}</span>;

    default:
      return <span className={alignClass}>{String(value)}</span>;
  }
}
